import type { UserEvent } from "./api.js";
import type { Locale } from "../i18n.js";

/** Render only known payload fields; older events may have incomplete data. */
export function notificationSummary(event: Pick<UserEvent, "type" | "payload">, locale: Locale): string {
  const zh = locale === "zh-CN";
  const data = event.payload && typeof event.payload === "object"
    ? event.payload as Record<string, unknown> : {};
  const name = typeof data.senderName === "string" && data.senderName.trim()
    ? data.senderName : zh ? "一位玩家" : "A player";
  const code = typeof data.destinationCode === "string" ? data.destinationCode : "";
  if (event.type.startsWith("invite.")) {
    const destination = data.destinationType === "party"
      ? zh ? "双人组队" : "a duo party"
      : zh ? "私人房间" : "a private room";
    const states: Record<string, string> = {
      pending: zh ? "等待处理" : "Awaiting response",
      accepting: zh ? "正在加入" : "Joining",
      accepted: zh ? "已接受" : "Accepted",
      declined: zh ? "已拒绝" : "Declined",
      cancelled: zh ? "已取消" : "Cancelled",
      expired: zh ? "已过期" : "Expired",
    };
    const status = event.type === "invite.created"
      ? zh ? "邀请已送达" : "Invitation delivered"
      : states[String(data.state)] ?? (zh ? "邀请状态已更新" : "Invitation updated");
    return `${zh ? `${name} 发起${destination}邀请` : `${name} sent an invitation to ${destination}`}${code ? ` · ${code}` : ""} · ${status}`;
  }
  switch (event.type) {
    case "friend.followed": return data.friends === true
      ? zh ? "你们已互相关注，现在可以邀请对方一起玩。" : "You now follow each other and can invite each other to play."
      : zh ? "有玩家关注了你，可在社交页的粉丝列表查看。" : "A player followed you. Find them in your followers list.";
    case "friend.unfollowed": return zh ? "关注关系已更新，社交列表已同步。" : "A follow relationship changed. Your social list is up to date.";
    case "reward.granted": {
      const rewards = [["coins", zh ? "金币" : "coins"], ["xp", "XP"], ["level", zh ? "等级" : "level"]]
        .filter(([key]) => typeof data[key!] === "number")
        .map(([key, label]) => `${label} ${data[key!]}`);
      return rewards.length ? rewards.join(" · ") : zh ? "奖励已发放，账户数据已更新。" : "Rewards received. Your account has been updated.";
    }
    case "room.ready": return zh ? "牌桌已准备就绪，可从大厅恢复入口进入对局。" : "Your table is ready. Resume the game from Home.";
    case "queue.updated": return zh ? "匹配进度已更新，可在大厅查看或取消匹配。" : "Matchmaking progress updated. Check or cancel matching from Home.";
    case "lobby.updated": return zh ? "房间成员或准备状态已变化，可返回房间查看。" : "Room membership or readiness changed. Return to your room for the current status.";
    case "activity.changed": return zh ? "游戏活动状态已更新，大厅会显示可恢复的活动。" : "Your game activity changed. Available activities appear on Home.";
    case "presence.changed": return data.online === true ? zh ? "一位玩家上线了。" : "A player came online." : zh ? "一位玩家离线了。" : "A player went offline.";
    case "feed.item": return zh ? "你关注的玩家发布了新动态，可在社交页查看。" : "A player you follow posted an update. Find it in the social feed.";
    default: return zh ? "账户有一条新通知。" : "You have an account notification.";
  }
}
